import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import { pluralizeWithCount } from "@common/helpers/string";
import { ChartOfAccountsRow } from "@components/accounting/chartOfAccounts/utils";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { CoaMappingPreviewSource } from "../buildCoaMappingPreview";
import {
    CoaMappingPreviewTableCallbacks,
    CoaMappingPreviewTableOptions,
} from "../coaMappingPreviewTableMeta";

const TOOLTIP_SOURCE_LIMIT = 5;

function sourceLabel(source: CoaMappingPreviewSource): string {
    return source.sourceNumber === null
        ? source.name
        : `${source.sourceNumber} - ${source.name}`;
}

function sourceBullets(sources: CoaMappingPreviewSource[]): string[] {
    const labels = sources.slice(0, TOOLTIP_SOURCE_LIMIT).map(sourceLabel);
    const hiddenCount = sources.length - TOOLTIP_SOURCE_LIMIT;
    return hiddenCount > 0 ? [...labels, `+${hiddenCount} more`] : labels;
}

export const CoaMappingPreviewSourcesCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, ChartOfAccountsRow, unknown>) => {
        const { options, callbacks } = useViewTableMeta<
            CoaMappingPreviewTableCallbacks,
            CoaMappingPreviewTableOptions
        >(table);
        const account = row.original.account;
        const sources = account
            ? options?.sourcesByAccountId.get(account.id)
            : undefined;
        if (!account || !sources || sources.length === 0) {
            return <ViewTableCell />;
        }
        return (
            <ViewTableCell captureClicks>
                <Tooltip bullets={sourceBullets(sources)}>
                    <Button
                        variant="link"
                        size="xs"
                        onClick={() =>
                            callbacks?.onViewMappedSources(account.id)
                        }
                    >
                        {pluralizeWithCount("account", sources.length)}
                    </Button>
                </Tooltip>
            </ViewTableCell>
        );
    },
);

CoaMappingPreviewSourcesCell.displayName = "CoaMappingPreviewSourcesCell";
